/**
 * A support ticket filed from a chat: what it says and who it is about.
 * Pure, so it is unit-tested on its own; SupportTicketSection sends it.
 */

import { CreateSupportTicketInput } from '../../hooks/chat/api';
import { riderLabel, systemLabel } from '../../hooks/chat/mappers';
import { Conversation, Message } from '../../hooks/useChat';
import { sliceWhole } from './clip';
import { statusLabel } from './format';

/** The ticket carries this many of the latest public messages. */
export const TICKET_MESSAGES = 10;

/** ant's SupportTicket.content max_length. */
export const TICKET_CONTENT_MAX = 20_000;

/** A chat body can be 4000 characters; each ticket line keeps this many. */
export const TICKET_LINE_MAX = 1500;

/**
 * `text` cut to `max` characters with "…" after, or as it is when it fits.
 * The cut never splits an emoji in two.
 */
export const clipText = (text: string, max: number): string =>
  text.length > max ? `${sliceWhole(text, max)}…` : text;

/** "2026-09-25 10:05 UTC": the same for everyone who reads the ticket. */
export function utcStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

/** admin_api's user id is the rider's phone, digits only; null when unknown. */
export function ticketUserOldId(phone: string | null | undefined): string | null {
  const digits = (phone ?? '').replace(/\D/g, '');
  return digits || null;
}

/** What the rider and Support said, never internal notes or system rows. */
const isPublic = (m: Message) =>
  m.sender_type === 'user' || m.sender_type === 'admin' || m.sender_type === 'assistant';

function messageLine(m: Message): string {
  const text = clipText(m.content.trim().replace(/\s*\n\s*/g, ' / '), TICKET_LINE_MAX);
  const media =
    m.content_type === 'video'
      ? '[video]'
      : m.content_type === 'image' || m.image_pending
      ? '[photo]'
      : '';
  const body = [text, media].filter(Boolean).join(' ') || '(empty)';
  return `[${utcStamp(m.created_on)}] ${m.sender_name}: ${body}`;
}

/**
 * The ticket's text: a short summary of the chat and its last public
 * messages, each line cut at TICKET_LINE_MAX characters. The oldest
 * messages give way until the whole fits in TICKET_CONTENT_MAX.
 */
export function buildTicketContent({
  conversation,
  messages,
  agentName,
  rentalOldId,
  now = new Date(),
}: {
  conversation: Conversation;
  messages: Message[];
  agentName: string;
  rentalOldId?: string | null;
  now?: Date;
}): string {
  const header = [
    `Created from Support Chat by ${agentName || 'Support'} on ${utcStamp(
      now.toISOString()
    )}.`,
    `Rider: ${riderLabel(conversation)}${
      conversation.user_phone ? ` (${conversation.user_phone})` : ''
    }`,
    `System: ${systemLabel(conversation)}`,
    ...(rentalOldId ? [`Rental: ${rentalOldId}`] : []),
    `Chat: ${statusLabel(conversation.status)}, opened ${utcStamp(
      conversation.created_on
    )}`,
    ...(conversation.priority === 'urgent' ? ['Priority: Urgent'] : []),
    ...(conversation.tags?.length ? [`Tags: ${conversation.tags.join(', ')}`] : []),
    '',
  ];
  const compose = (lines: string[]) =>
    [
      ...header,
      lines.length
        ? `Last ${lines.length === 1 ? 'message' : `${lines.length} messages`}:`
        : 'No messages yet.',
      ...lines,
    ].join('\n');

  let lines = messages.filter(isPublic).slice(-TICKET_MESSAGES).map(messageLine);
  let content = compose(lines);
  while (content.length > TICKET_CONTENT_MAX && lines.length > 0) {
    lines = lines.slice(1);
    content = compose(lines);
  }
  // Only a header past the limit (hundreds of tags) is left: cut it.
  return content.length > TICKET_CONTENT_MAX
    ? clipText(content, TICKET_CONTENT_MAX - 1)
    : content;
}

/**
 * The createSupportTicket input for a chat: the rider by phone, the system
 * and rental by admin_api id when the chat has them. Null when the rider's
 * phone is not known (a ticket needs its user).
 */
export function supportTicketInput({
  conversation,
  content,
  systemOldId,
  rentalOldId,
}: {
  conversation: Conversation;
  content: string;
  systemOldId?: string | null;
  rentalOldId?: string | null;
}): CreateSupportTicketInput | null {
  const userOldId = ticketUserOldId(conversation.user_phone);
  if (!userOldId) return null;
  return {
    userOldId,
    ...(systemOldId ? { systemOldId } : {}),
    ...(rentalOldId ? { rentalOldId } : {}),
    content,
  };
}

/** The internal note the chat keeps of a ticket filed from it. */
export const ticketNoteText = (ticketNumber: string): string =>
  `Support ticket #${ticketNumber} created`;
